import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface FunnelStep {
  stage: string;
  count: number;
  rate: number | null;
}

export interface FunnelOutcomes {
  rejected: number;
  withdrawn: number;
  offerDeclined: number;
}

const outcomeStages: { key: keyof FunnelOutcomes; labelKey: string; barColor: string }[] = [
  { key: 'rejected', labelKey: 'dashboard.stateRejected', barColor: 'bg-destructive/70' },
  { key: 'withdrawn', labelKey: 'dashboard.stateWithdrawn', barColor: 'bg-muted-foreground/50' },
  { key: 'offerDeclined', labelKey: 'dashboard.stateOfferDeclined', barColor: 'bg-warning/70' },
];

const stageKeys: Record<string, string> = {
  Applied: 'dashboard.funnelApplied',
  Screened: 'dashboard.funnelScreened',
  Interview: 'dashboard.funnelInterview',
  Decision: 'dashboard.funnelDecision',
  Hired: 'dashboard.funnelHired',
};

const tooltipKeys: Record<string, string> = {
  Screened: 'dashboard.tooltipScreened',
  Interview: 'dashboard.tooltipInterview',
  Decision: 'dashboard.tooltipDecision',
};

const barColors = [
  'bg-primary/80',
  'bg-primary/60',
  'bg-warning',
  'bg-success',
];

function FunnelRow({
  label,
  count,
  pct,
  barColor,
  tooltip,
}: {
  label: string;
  count: number;
  pct: number;
  barColor: string;
  tooltip?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-medium">{label}</span>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 shrink-0 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px]">
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-semibold tabular-nums">{count}</span>
          <span className="text-muted-foreground w-10 text-right tabular-nums">{pct.toFixed(0)}%</span>
        </div>
      </div>
      <div className="h-5 rounded bg-muted overflow-hidden">
        <div
          className={`h-full rounded transition-all ${barColor}`}
          style={{ width: `${pct}%`, minWidth: count > 0 ? '4px' : '0' }}
        />
      </div>
    </div>
  );
}

export const FunnelChart = ({
  data,
  outcomes,
  className,
}: {
  data: FunnelStep[];
  outcomes?: FunnelOutcomes;
  className?: string;
}) => {
  const { t } = useLanguage();
  const applied = data[0];
  const stages = data.slice(1);
  const maxCount = applied?.count || 1;

  if (!applied || applied.count === 0) {
    return (
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">{t('dashboard.recruitmentFunnel')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.noDataPeriod')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-border/50 ${className || ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{t('dashboard.recruitmentFunnel')}</CardTitle>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs text-muted-foreground">{t(stageKeys.Applied)}</span>
            <span className="text-lg font-bold">{applied.count}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <TooltipProvider>
          {stages.map((step, i) => {
            const pct = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
            return (
              <FunnelRow
                key={step.stage}
                label={t(stageKeys[step.stage] || step.stage)}
                count={step.count}
                pct={pct}
                barColor={barColors[i] || barColors[0]}
                tooltip={tooltipKeys[step.stage] ? t(tooltipKeys[step.stage]) : undefined}
              />
            );
          })}

          {outcomes &&
            outcomeStages.map(({ key, labelKey, barColor }) => {
              const count = outcomes[key];
              const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
              return (
                <FunnelRow
                  key={key}
                  label={t(labelKey)}
                  count={count}
                  pct={pct}
                  barColor={barColor}
                />
              );
            })}
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};
